import React from 'react';
import { Chip, Stack, Typography } from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { useBookingCopyTranslation as useTranslation } from '../../i18n/bookingCopy';
import { PaymentStatus, StripeRefundStatus } from '../../models/RoomBooking';
import {
  getTutorPaymentStatusChipColor,
  getTutorRefundStatusChipColor,
  getTutorRefundStatusChipLabel,
} from '../../utils/tutorBookingPayment';

export interface TutorBookingPaymentStatusChipsProps {
  paymentStatus?: PaymentStatus | string | null;
  stripeRefundStatus?: StripeRefundStatus | null;
  refundReason?: string | null;
  showRefundReason?: boolean;
  /** Smaller outlined chips for dense booking cards. */
  compact?: boolean;
}

const compactChipSx = {
  height: 22,
  fontWeight: 500,
  '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem', lineHeight: 1.2 },
  '& .MuiChip-icon': { fontSize: 14, ml: 0.5, mr: -0.25 },
} as const;

export default function TutorBookingPaymentStatusChips({
  paymentStatus,
  stripeRefundStatus,
  refundReason,
  showRefundReason = false,
  compact = false,
}: TutorBookingPaymentStatusChipsProps) {
  const { t } = useTranslation();

  if (!paymentStatus && !stripeRefundStatus) {
    return null;
  }

  return (
    <Stack
      direction={compact ? 'row' : 'column'}
      spacing={compact ? 0.5 : 0.75}
      alignItems="flex-start"
      flexWrap="wrap"
      useFlexGap
    >
      {paymentStatus && !stripeRefundStatus ? (
        <Chip
          size="small"
          variant={compact ? 'outlined' : 'filled'}
          label={t(`paymentStatus.${String(paymentStatus).toLowerCase()}`, String(paymentStatus))}
          color={getTutorPaymentStatusChipColor(String(paymentStatus))}
          sx={compact ? compactChipSx : undefined}
        />
      ) : null}
      {stripeRefundStatus ? (
        <Chip
          size="small"
          variant={compact ? 'outlined' : 'filled'}
          icon={<ReceiptIcon fontSize="small" />}
          label={getTutorRefundStatusChipLabel(stripeRefundStatus, t)}
          color={getTutorRefundStatusChipColor(stripeRefundStatus)}
          sx={compact ? compactChipSx : undefined}
        />
      ) : null}
      {showRefundReason && refundReason?.trim() ? (
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.35 }}>
          {t('hostTutorBookings.refundReasonNote', 'Refund note: {{reason}}', {
            reason: refundReason.trim(),
          })}
        </Typography>
      ) : null}
    </Stack>
  );
}
